// src/lib/seitenTitel.ts
// Seitentitel und Meta-Description je Route (Paket 22, 20.10.2026). Vorher hieß jede
// Seite „PROPORA Immo-Tools“ – im Browser-Tab, in Lesezeichen und in GA4 nicht unterscheidbar.
// Format wie auf www.propora.de: „<Seite> | PROPORA“.

export type SeitenMeta = { titel: string; beschreibung: string };

export const MARKE = "PROPORA";

export const STANDARD_META: SeitenMeta = {
  titel: "PROPORA – Immobilie als Kapitalanlage prüfen",
  beschreibung:
    "Prüfe eine Immobilie als Kapitalanlage: Deal-Score, Cashflow, Finanzierung und Stresstest aus deinen Zahlen. Kostenloser Schnell-Check in 60 Sekunden.",
};

const SEITEN: Record<string, { name: string; beschreibung?: string }> = {
  "/check": {
    name: "Schnell-Check: Lohnt sich die Immobilie?",
    beschreibung:
      "Kaufpreis, Miete und Eigenkapital eingeben – Deal-Score, monatlicher Cashflow und der Kaufpreis, ab dem sich die Immobilie trägt. Kostenlos, in 60 Sekunden.",
  },
  "/deals": { name: "Meine Objekte" },
  "/wohnung": {
    name: "Eigentumswohnung prüfen",
    beschreibung: "Eigentumswohnung als Kapitalanlage durchrechnen: Hausgeld, Rücklage, Finanzierung, Cashflow und Stresstest.",
  },
  "/mfh": {
    name: "Mehrfamilienhaus prüfen",
    beschreibung: "Mehrfamilienhaus als Kapitalanlage durchrechnen: Einheiten, Bewirtschaftung, Finanzierung, DSCR und Stresstest.",
  },
  "/einfamilienhaus": {
    name: "Einfamilienhaus prüfen",
    beschreibung: "Einfamilienhaus als Kapitalanlage durchrechnen: Miete, Kosten, Finanzierung und Cashflow.",
  },
  "/gemischte-immobilie": {
    name: "Wohn- und Geschäftshaus prüfen",
    beschreibung: "Gemischt genutzte Immobilie durchrechnen: Wohn- und Gewerbeflächen getrennt, Finanzierung und Stresstest.",
  },
  "/gewerbe": {
    name: "Gewerbeimmobilie prüfen",
    beschreibung: "Gewerbeimmobilie durchrechnen: Mietflächen, Leerstand, Bewirtschaftung, Finanzierung und Schuldendeckung.",
  },
  "/miete": { name: "Mietkalkulation", beschreibung: "Miete einer Immobilie kalkulieren: Kaltmiete, Nebenkosten und Rendite auf einen Blick." },
  "/finanzierung-simpel": {
    name: "Finanzierungsrechner",
    beschreibung: "Immobilienfinanzierung rechnen: Rate, Zins, Tilgung und Restschuld für deine Kapitalanlage.",
  },
  "/finanzierungsvergleich": {
    name: "Finanzierungsvergleich",
    beschreibung: "Bis zu 5 Finanzierungsangebote für deine Immobilie nebeneinander vergleichen: Rate, Zins und Restschuld.",
  },
  "/afa": { name: "AfA-Rechner", beschreibung: "Abschreibung (AfA) deiner vermieteten Immobilie berechnen." },
  "/vergleich": { name: "Objekte vergleichen" },
  "/preise": {
    name: "Preise",
    beschreibung: "PROPORA FREE und PRO im Überblick: kostenlos starten, mit PRO alle Hebel, 10-Jahres-Prognose und Bankbericht als PDF.",
  },
  "/checkout": { name: "Bezahlen" },
  "/upgrade": { name: "PRO freischalten" },
  "/extension": { name: "Browser-Erweiterung" },
  "/login": { name: "Anmelden" },
  "/register": { name: "Kostenlos registrieren" },
  "/logout": { name: "Abmelden" },
  "/account": { name: "Mein Konto" },
  "/changelog": { name: "Neuigkeiten" },
};

/** Titel und Beschreibung für einen Pfad. Unbekannte Pfade bekommen den Standard. */
export function seitenMetaFuer(pathname: string, objektName?: string | null): SeitenMeta {
  const pfad = pathname.replace(/\/+$/, "") || "/";
  if (pfad.startsWith("/deal/")) {
    const name = objektName?.trim();
    return { titel: `${name ? name : "Objekt"} | ${MARKE}`, beschreibung: STANDARD_META.beschreibung };
  }
  const s = SEITEN[pfad];
  if (!s) return STANDARD_META;
  return { titel: `${s.name} | ${MARKE}`, beschreibung: s.beschreibung ?? STANDARD_META.beschreibung };
}

/** Setzt <title> und <meta name="description"> im Browser. */
export function setzeSeitenMeta(meta: SeitenMeta): void {
  if (typeof document === "undefined") return;
  document.title = meta.titel;
  let tag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.name = "description";
    document.head.appendChild(tag);
  }
  tag.content = meta.beschreibung;
}
